// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n, getCurrencySymbol } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import CreatePurchaseOrderModal from 'main-app/components/CreatePurchaseOrderModal';
import moment from 'moment';

type Props = {
  history: RouterHistory,
};

type State = {
  createModalOpen: boolean,
  statusFilter: ?string,
};

const FilterSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0 24px 16px 24px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const ListWrapper = styled.div`
  flex: 1;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.status) {
      case 'PENDING_APPROVAL':
        return '#fff3cd';
      case 'APPROVED':
        return '#cfe2ff';
      case 'SENT':
        return '#e2d9f3';
      case 'PARTIALLY_RECEIVED':
        return '#ffe4b5';
      case 'RECEIVED':
        return '#d1f2dd';
      case 'CANCELLED':
      case 'REJECTED':
        return '#f8d7da';
      default:
        return '#e0e0e0';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'PENDING_APPROVAL':
        return '#856404';
      case 'APPROVED':
        return '#084298';
      case 'SENT':
        return '#6f42c1';
      case 'PARTIALLY_RECEIVED':
        return '#cc5500';
      case 'RECEIVED':
        return '#0f5132';
      case 'CANCELLED':
      case 'REJECTED':
        return '#842029';
      default:
        return '#666';
    }
  }};
`;

class PurchaseOrderList extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      createModalOpen: false,
      statusFilter: null,
    };
  }

  handleOpenCreateModal = () => {
    this.setState({
      createModalOpen: true,
    });
  };

  handleCloseModal = () => {
    this.setState({
      createModalOpen: false,
    });
  };

  handleStatusFilterChange = (e: SyntheticInputEvent<HTMLSelectElement>) => {
    this.setState({
      statusFilter: e.target.value || null,
    });
  };

  handleRowClick = (poId: string) => {
    const { history } = this.props;
    history.push(`/purchase-order/${poId}`);
  };

  formatCurrency = (amount: ?number) => {
    if (!amount) return `${getCurrencySymbol()}0.00`;
    return `${getCurrencySymbol()}${parseFloat(amount).toFixed(2)}`;
  };

  formatDate = (date: ?string) => {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY');
  };

  render() {
    const { history } = this.props;
    const { createModalOpen, statusFilter } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query PurchaseOrderListQuery($status: POStatus) {
            purchaseOrders(first: null, status: $status)
              @connection(
                key: "PurchaseOrderList_purchaseOrders"
                filters: ["status"]
              ) {
              edges {
                node {
                  id
                  poNumber
                  vendor {
                    id
                    vendorNumber
                    name
                  }
                  status
                  totalAmount
                  expectedDeliveryDate
                  createdAt
                  updatedAt
                }
              }
            }
          }
        `}
        variables={{ status: statusFilter }}
        render={({ props }) => {
          return (
            <Page>
              <PageHeaderList
                title={i18n.t('Purchase Orders')}
                createButtonLabel={props && i18n.t('Create Purchase Order')}
                onCreateButtonClick={this.handleOpenCreateModal}
              />

              <FilterSection>
                <label style={{ fontSize: 14, color: '#666' }}>
                  {i18n.t('Filter by Status:')}
                </label>
                <FilterSelect
                  value={statusFilter || ''}
                  onChange={this.handleStatusFilterChange}
                >
                  <option value="">{i18n.t('All Statuses')}</option>
                  <option value="PENDING_APPROVAL">
                    {i18n.t('Pending Approval')}
                  </option>
                  <option value="APPROVED">{i18n.t('Approved')}</option>
                  <option value="SENT">{i18n.t('Sent')}</option>
                  <option value="PARTIALLY_RECEIVED">
                    {i18n.t('Partially Received')}
                  </option>
                  <option value="RECEIVED">{i18n.t('Received')}</option>
                  <option value="CANCELLED">{i18n.t('Cancelled')}</option>
                </FilterSelect>
              </FilterSection>

              {!props ? (
                <Loader />
              ) : !props.purchaseOrders.edges.length ? (
                <EmptyState>
                  {statusFilter
                    ? i18n.t('No purchase orders found with this status.')
                    : i18n.t("You haven't created any purchase orders yet.")}
                </EmptyState>
              ) : (
                <ListWrapper>
                  <Table
                    data={props.purchaseOrders.edges.map(edge => edge.node)}
                    onRowClick={row => this.handleRowClick(row.id)}
                    columns={[
                      {
                        Header: i18n.t('PO Number'),
                        accessor: 'poNumber',
                        width: 140,
                      },
                      {
                        Header: i18n.t('Vendor'),
                        accessor: 'vendor.name',
                        width: 200,
                      },
                      {
                        Header: i18n.t('Status'),
                        accessor: 'status',
                        width: 180,
                        Cell: ({ value }) => (
                          <StatusBadge status={value}>
                            {value.replace(/_/g, ' ')}
                          </StatusBadge>
                        ),
                      },
                      {
                        Header: i18n.t('Total Amount'),
                        accessor: 'totalAmount',
                        width: 140,
                        Cell: ({ value }) => this.formatCurrency(value),
                      },
                      {
                        Header: i18n.t('Expected Delivery'),
                        accessor: 'expectedDeliveryDate',
                        width: 160,
                        Cell: ({ value }) => this.formatDate(value),
                      },
                      {
                        Header: i18n.t('Created'),
                        accessor: 'createdAt',
                        width: 140,
                        Cell: ({ value }) => this.formatDate(value),
                      },
                    ]}
                  />
                </ListWrapper>
              )}

              {createModalOpen && (
                <CreatePurchaseOrderModal
                  history={history}
                  onClose={this.handleCloseModal}
                />
              )}
            </Page>
          );
        }}
      />
    );
  }
}

export default PurchaseOrderList;
